// @ts-strict-ignore
import { IMoveArticlesOperation } from './i-move-articles-operation';
import { IOperation } from './i-operation';
import { ProjectModel } from '@project/model/project.model';
import { Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { NumberUtils } from '../../util/number-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { Dictionary } from 'typescript-collections';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';

export class UndoResizeArticleOperation implements IMoveArticlesOperation {
  private _articleGUID: string;
  private _lengthDelta: number;
  private _undoDrillingHolesOperation: IOperation;
  private _undoMoveArticlesOperation: IMoveArticlesOperation;
  private _projectModel: ProjectModel;

  constructor(
    articleGUID: string,
    lengthDelta: number,
    private selectionModelService: SelectionModelService,
    private containerService: ContainerService,
    private _applicationRefreshService: ApplicationRefreshService,
    undoDrillingHolesOperation: IOperation = null,
    undoMoveArticlesOperation: IMoveArticlesOperation = null,
    projectModel: ProjectModel = null
  ) {
    this._articleGUID = articleGUID;
    this._lengthDelta = lengthDelta;
    this._undoDrillingHolesOperation = undoDrillingHolesOperation;
    this._undoMoveArticlesOperation = undoMoveArticlesOperation;
    this._projectModel = projectModel;
  }

  public getMoveArticlesMap(): Dictionary<string, Vector3> {
    return this._undoMoveArticlesOperation ? this._undoMoveArticlesOperation.getMoveArticlesMap() : null;
  }

  public isArticlesOrderChanged(): boolean {
    return this._undoMoveArticlesOperation ? this._undoMoveArticlesOperation.isArticlesOrderChanged() : false;
  }

  public isDistanceBetweenArticlesChanged(): boolean {
    return this._undoMoveArticlesOperation ? this._undoMoveArticlesOperation.isDistanceBetweenArticlesChanged() : false;
  }

  public getQualifiedClassName(): string {
    return 'UndoResizeArticleOperation';
  }

  public getOperationDescription(): string {
    return '';
  }

  public async performRedo(): Promise<void> {
    if (this._projectModel) {
      this._projectModel.isMountingRailInProcess = true;
    }
    await this.resizeArticle(this._lengthDelta);
    if (this._undoMoveArticlesOperation) {
      await this._undoMoveArticlesOperation.performRedo();
    }
    if (this._undoDrillingHolesOperation) {
      await this._undoDrillingHolesOperation.performRedo();
    }
    if (this._projectModel) {
      this._projectModel.isMountingRailInProcess = false;
    }
  }

  public async performUndo(): Promise<void> {
    if (this._projectModel) {
      this._projectModel.isMountingRailInProcess = true;
    }
    await this.resizeArticle(-this._lengthDelta);
    if (this._undoMoveArticlesOperation) {
      await this._undoMoveArticlesOperation.performUndo();
    }
    if (this._undoDrillingHolesOperation) {
      await this._undoDrillingHolesOperation.performUndo();
    }
    if (this._projectModel) {
      this._projectModel.isMountingRailInProcess = false;
    }
  }

  private async resizeArticle(sizeDelta: number): Promise<void> {
    this.selectionModelService.removeAllSelections();
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._articleGUID);
    if (articleContainer) {
      let currentLength: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
        articleContainer.articleUsage,
        ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
      );
      if (NumberUtils.isNumber(currentLength)) {
        currentLength = currentLength + sizeDelta;
        ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
          articleContainer.articleUsage,
          ArticleUsageInfoPropertyDef.ARTICLE_LENGTH,
          currentLength
        );
        articleContainer.disposeModel();
        // this._existingMountingRail.isParsed = false;
        articleContainer.isParsed = false;
        await this._applicationRefreshService.refreshProject();
        if (ArticleUtils.isMountingRail(articleContainer.article)) {
          const mountingRailContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleContainer.articleUsage.id);
          const railWasStretched: boolean = ArticlesCreator.updateRangeOnLineConnection(
            mountingRailContainer,
            mountingRailContainer.article,
            RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS
          );
          if (railWasStretched) {
            ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(articleContainer.articleUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED, true);
            ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
              articleContainer.articleUsage,
              ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES,
              RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS_ON_BOTH_SIDES
            );
          } else {
            ArticleUsageUtils.removeArticleUsageInfoProperty(articleContainer.articleUsage, ArticleUsageInfoPropertyDef.IS_STRETCHED);
            ArticleUsageUtils.removeArticleUsageInfoProperty(articleContainer.articleUsage, ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES);
          }
        }
      }
    }
  }
}
